<template>
  <div class="member-tickets-wrap ">
    
    <v-row>
      <!-- ── Active Tickets ──────────────────── -->
      <v-col cols="12" md="6">
        <PremiumTable
          title="Active Support Tickets"
          :show-view="true"
          @view="$router.push('/societyadmin/complaints/view/' + $event.id)"
          :columns="ticketColumns"
          :items="tickets.active"
          :loading="loading"
          :total-items="activeTotal"
          :page.sync="activeOptions.page"
          :items-per-page.sync="activeOptions.itemsPerPage"
          :searchable="false"
          @update:options="onActiveOptionsUpdate"
        >
          <template #col-title="{ item }">
            <div class="ticket-title-group">
              <span class="ticket-subject">{{ item.title }}</span>
              <span class="ticket-meta">By {{ item.created_by_name || 'Member' }}</span>
            </div>
          </template>

          <template #col-priority="{ item }">
            <div class="priority-pill-mini" :style="getPriorityStyles(item.priority)">
              <span class="priority-dot-mini"></span>
              {{ getPriorityLabel(item.priority) }}
            </div>
          </template>

          <template #col-status="{ item }">
            <v-menu offset-y transition="slide-y-transition" bottom right>
              <template v-slot:activator="{ on, attrs }">
                <div
                  class="status-pill-mini"
                  v-bind="attrs"
                  v-on="on"
                  :style="getStatusStyles(item.status)"
                >
                  <span class="status-dot-mini" :style="{ background: getStatusColor(item.status) }"></span>
                  <span class="status-label-mini">{{ getStatusLabel(item.status) }}</span>
                  <v-icon size="10" :color="getStatusColor(item.status)" class="ml-1 opacity-60">mdi-chevron-down</v-icon>
                </div>
              </template>
              <v-list dense class="py-1 rounded-lg">
                <v-list-item
                  v-for="opt in statusOptions"
                  :key="opt.value"
                  @click="updateStatus(item.id, opt.value)"
                  class="rounded-md mb-1 mx-1"
                  style="min-height: 36px"
                >
                  <div class="status-dot-mini mr-2" :style="{ background: opt.color }"></div>
                  <v-list-item-title class="font-weight-bold" style="font-size: 12px">{{ opt.name }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>

          <template #col-created_at="{ item }">
            <span class="date-text-mini">{{ item.created_at | formatDate }}</span>
          </template>

        </PremiumTable>
      </v-col>

      <!-- ── Closed Tickets ──────────────────── -->
      <v-col cols="12" md="6">
        <PremiumTable
          title="Resolved / Closed"
          :show-view="true"
          @view="$router.push('/societyadmin/complaints/view/' + $event.id)"
          :columns="closedColumns"
          :items="tickets.closed"
          :loading="loading"
          :total-items="closedTotal"
          :page.sync="closedOptions.page"
          :items-per-page.sync="closedOptions.itemsPerPage"
          :searchable="false"
          @update:options="onClosedOptionsUpdate"
        >
          <template #col-title="{ item }">
            <span class="ticket-subject-closed">{{ item.title }}</span>
          </template>

          <template #col-priority="{ item }">
            <div class="priority-pill-mini" :style="getPriorityStyles(item.priority)">
              <span class="priority-dot-mini"></span>
              {{ getPriorityLabel(item.priority) }}
            </div>
          </template>

          <template #col-status="{ item }">
            <div class="status-pill-mini no-cursor" :style="getStatusStyles(item.status)">
              <span class="status-dot-mini" :style="{ background: getStatusColor(item.status) }"></span>
              <span class="status-label-mini">{{ getStatusLabel(item.status) }}</span>
            </div>
          </template>

          <template #col-created_at="{ item }">
            <span class="date-text-mini">{{ item.created_at | formatDate }}</span>
          </template>

        </PremiumTable>
      </v-col>
    </v-row>

  </div>
</template>

<script>
import { mapMutations } from "vuex";
import PremiumTable from '../../common/PremiumTable.vue';

export default {
  name: 'MemberTickets',

  components: { PremiumTable },

  props: {
    unit_mapping_id: { type: [String, Number], required: true }
  },

  data() {
    return {
      loading: false,
      tickets: {
        active: [],
        closed: []
      },
      activeTotal: 0,
      activeOptions: { page: 1, itemsPerPage: 5 },
      closedTotal: 0,
      closedOptions: { page: 1, itemsPerPage: 5 },
      statusOptions: [
        { name: "Pending",     value: 0, color: "#F59E0B" },
        { name: "In Progress", value: 1, color: "#10B981" },
        { name: "Completed",   value: 2, color: "#3B82F6" },
        { name: "Cancelled",   value: 3, color: "#EF4444" },
      ],
      ticketColumns: [
        { key: 'title',    label: 'Ticket Title' },
        { key: 'priority', label: 'Priority', width: '90px' },
        { key: 'status',   label: 'Status', width: '130px' },
        { key: 'created_at', label: 'Date', width: '120px' },
      ],
      closedColumns: [
        { key: 'title',    label: 'Ticket Title' },
        { key: 'priority', label: 'Priority', width: '90px' },
        { key: 'status',   label: 'Status', width: '110px' },
        { key: 'created_at', label: 'Date', width: '120px' },
      ]
    };
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),

    onActiveOptionsUpdate() { this.fetchTickets(); },
    onClosedOptionsUpdate() { this.fetchTickets(); },

    async fetchTickets() {
      if (this.loading) return;
      this.loading = true;
      try {
        const response = await this.$axios.get(`societyadmin/members/${this.unit_mapping_id}/tickets`, {
          params: {
            activePage: this.activeOptions.page,
            closedPage: this.closedOptions.page,
            perPage: 5
          }
        });
        if (response.data.status) {
          this.tickets = response.data.tickets;
          this.activeTotal = response.data.tickets.activeTotal;
          this.closedTotal = response.data.tickets.closedTotal;
        }
      } catch (error) {
        console.error("Error fetching tickets:", error);
      } finally {
        this.loading = false;
      }
    },

    async updateStatus(id, status) {
      try {
        const response = await this.$axios.put(`societyadmin/complaints/updateStatus/${id}/${status}`);
        if (response.data.status) {
          this.setAlert({ showAlert: true, variant: "green accent-4", timeout: "2000", message: response.data.message });
          this.fetchTickets();
        } else {
          this.setAlert({ showAlert: true, variant: "red accent-3", timeout: "2000", message: response.data.message });
        }
      } catch (error) {
        console.error("Error updating status:", error);
      }
    },

    getPriorityLabel(val) {
      const labels = { 0: 'Emergency', 1: 'Low', 2: 'Medium', 3: 'High' };
      return labels[val] || '';
    },
    getPriorityStyles(val) {
      const colors = { 0: '#EF4444', 1: '#64748B', 2: '#3B82F6', 3: '#F59E0B' };
      const color = colors[val] || '#64748B';
      return { 
        background: color + '12', 
        color: color,
        border: `1px solid ${color}20` 
      };
    },
    getStatusLabel(val) {
      const opt = this.statusOptions.find(o => o.value === Number(val));
      return opt ? opt.name : 'Unknown';
    },
    getStatusColor(val) {
      const opt = this.statusOptions.find(o => o.value === Number(val));
      return opt ? opt.color : '#64748B';
    },
    getStatusStyles(val) {
      const color = this.getStatusColor(val);
      return {
        background: color + '12',
        color: color,
        border: `1px solid ${color}20`
      };
    },

  },

  mounted() {
    this.fetchTickets();
  }
};
</script>

<style scoped>
.table-header-box { display: flex; align-items: center; justify-content: space-between; }
.th-title-wrap { display: flex; align-items: center; }
.th-title {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 15px; font-weight: 700; color: var(--tx);
  letter-spacing: -0.01em; margin: 0;
}

/* ── Active Ticket Cells ── */
.ticket-title-group { display: flex; flex-direction: column; gap: 2px; }
.ticket-subject { font-size: 13px; font-weight: 700; color: var(--tx); }
.ticket-meta { font-size: 11px; color: var(--tx3); }

.priority-pill-mini {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px 3px 7px;
  border-radius: 20px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.01em;
  user-select: none;
}
.priority-dot-mini {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  flex-shrink: 0;
  background: currentColor;
  opacity: 0.8;
}

/* ── Closed Ticket Cells ── */
.ticket-subject-closed { font-size: 13.5px; font-weight: 600; color: var(--tx2); }
.no-cursor { cursor: default !important; }

.date-text-mini { font-size: 11px; color: var(--tx3); font-weight: 600; }

/* ── Status Pill Mini ── */
.status-pill-mini {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 8px 4px 6px;
  border-radius: 20px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.01em;
  cursor: pointer;
  user-select: none;
  transition: all 0.15s;
  white-space: nowrap;
}
.status-pill-mini:hover {
  filter: brightness(0.96);
  transform: translateY(-1px);
}
.status-dot-mini {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  flex-shrink: 0;
}
.status-label-mini {
  line-height: 1;
}

.rounded-md { border-radius: 8px !important; }
.opacity-60 { opacity: 0.6; }

</style>
